import { useState, useCallback, useEffect } from 'react';
import {
  PageContainer,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  useApiClient,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Alert,
  AlertDescription,
} from '@startupquickstart/frontend';
import {
  RefreshCw,
  Users,
  Building2,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
} from 'lucide-react';

interface SyncUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  clerkId: string | null;
  imageUrl: string | null;
  syncStatus: string;
  lastSyncError: string | null;
  lastSyncedAt: string | null;
  createdAt: string;
}

interface SyncOrganization {
  id: string;
  name: string;
  slug: string | null;
  clerkId: string | null;
  imageUrl: string | null;
  isInternal: boolean;
  syncStatus: string;
  lastSyncError: string | null;
  lastSyncedAt: string | null;
  createdAt: string;
}

interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

function SyncStatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'synced':
      return (
        <Badge variant="outline" className="border-green-200 bg-green-50 text-green-700">
          <CheckCircle2 className="mr-1 h-3 w-3" />
          Synced
        </Badge>
      );
    case 'error':
      return (
        <Badge variant="outline" className="border-red-200 bg-red-50 text-red-700">
          <XCircle className="mr-1 h-3 w-3" />
          Error
        </Badge>
      );
    default:
      return (
        <Badge variant="outline" className="border-zinc-200 bg-zinc-50 text-zinc-600">
          <Clock className="mr-1 h-3 w-3" />
          Pending
        </Badge>
      );
  }
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  return new Date(dateStr).toLocaleString();
}

/**
 * Clerk Sync management page for internal users.
 * Shows users and organizations with sync status and manual sync controls.
 */
export function ClerkSyncPage() {
  const apiClient = useApiClient();

  // Users state
  const [users, setUsers] = useState<SyncUser[]>([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(1);
  const [usersLoading, setUsersLoading] = useState(false);

  // Organizations state
  const [orgs, setOrgs] = useState<SyncOrganization[]>([]);
  const [orgsTotal, setOrgsTotal] = useState(0);
  const [orgsPage, setOrgsPage] = useState(1);
  const [orgsLoading, setOrgsLoading] = useState(false);

  // Sync state
  const [syncAllLoading, setSyncAllLoading] = useState(false);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<string | null>(null);

  // Stats
  const [syncedCount, setSyncedCount] = useState(0);
  const [errorCount, setErrorCount] = useState(0);

  const fetchUsers = useCallback(
    async (page = 1) => {
      setUsersLoading(true);
      setError(null);
      try {
        const response = (await apiClient.get(
          `/clerk-sync/users?page=${page}&limit=50`,
        )) as { data: PaginatedResponse<SyncUser> };
        setUsers(response.data.data);
        setUsersTotal(response.data.pagination.total);
        setUsersPage(page);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch users');
      } finally {
        setUsersLoading(false);
      }
    },
    [apiClient],
  );

  const fetchOrgs = useCallback(
    async (page = 1) => {
      setOrgsLoading(true);
      setError(null);
      try {
        const response = (await apiClient.get(
          `/clerk-sync/organizations?page=${page}&limit=50`,
        )) as { data: PaginatedResponse<SyncOrganization> };
        setOrgs(response.data.data);
        setOrgsTotal(response.data.pagination.total);
        setOrgsPage(page);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch organizations');
      } finally {
        setOrgsLoading(false);
      }
    },
    [apiClient],
  );

  const fetchStats = useCallback(async () => {
    try {
      const [syncedUsersRes, errorUsersRes, syncedOrgsRes, errorOrgsRes] =
        await Promise.all([
          apiClient.get('/clerk-sync/users?status=synced&limit=1') as Promise<{
            data: PaginatedResponse<SyncUser>;
          }>,
          apiClient.get('/clerk-sync/users?status=error&limit=1') as Promise<{
            data: PaginatedResponse<SyncUser>;
          }>,
          apiClient.get('/clerk-sync/organizations?status=synced&limit=1') as Promise<{
            data: PaginatedResponse<SyncOrganization>;
          }>,
          apiClient.get('/clerk-sync/organizations?status=error&limit=1') as Promise<{
            data: PaginatedResponse<SyncOrganization>;
          }>,
        ]);

      setSyncedCount(
        syncedUsersRes.data.pagination.total + syncedOrgsRes.data.pagination.total,
      );
      setErrorCount(
        errorUsersRes.data.pagination.total + errorOrgsRes.data.pagination.total,
      );
    } catch {
      // Stats are non-critical
    }
  }, [apiClient]);

  // Load initial data
  useEffect(() => {
    fetchUsers();
    fetchOrgs();
    fetchStats();
  }, [fetchUsers, fetchOrgs, fetchStats]);

  const handleSyncUser = useCallback(
    async (userId: string) => {
      setSyncingId(userId);
      try {
        await apiClient.post(`/clerk-sync/users/${userId}/sync`);
        await fetchUsers(usersPage);
        await fetchStats();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to sync user');
      } finally {
        setSyncingId(null);
      }
    },
    [apiClient, fetchUsers, fetchStats, usersPage],
  );

  const handleSyncOrg = useCallback(
    async (orgId: string) => {
      setSyncingId(orgId);
      try {
        await apiClient.post(`/clerk-sync/organizations/${orgId}/sync`);
        await fetchOrgs(orgsPage);
        await fetchStats();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to sync organization');
      } finally {
        setSyncingId(null);
      }
    },
    [apiClient, fetchOrgs, fetchStats, orgsPage],
  );

  const handleSyncAll = useCallback(async () => {
    setSyncAllLoading(true);
    setError(null);
    setSyncResult(null);
    try {
      const response = (await apiClient.post('/clerk-sync/sync-all')) as {
        data: {
          users: { synced: number; errors: number };
          organizations: { synced: number; errors: number };
        };
      };
      const { users: u, organizations: o } = response.data;
      setSyncResult(
        `Synced ${u.synced} users (${u.errors} errors), ${o.synced} organizations (${o.errors} errors)`,
      );
      await Promise.all([fetchUsers(1), fetchOrgs(1), fetchStats()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sync all');
    } finally {
      setSyncAllLoading(false);
    }
  }, [apiClient, fetchUsers, fetchOrgs, fetchStats]);

  return (
    <PageContainer>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Clerk Sync</h1>
            <p className="text-muted-foreground mt-2">
              Manage user and organization synchronization with Clerk
            </p>
          </div>
          <Button onClick={handleSyncAll} disabled={syncAllLoading}>
            {syncAllLoading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Sync All
          </Button>
        </div>

        {/* Alerts */}
        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {syncResult && (
          <Alert>
            <CheckCircle2 className="h-4 w-4" />
            <AlertDescription>{syncResult}</AlertDescription>
          </Alert>
        )}

        {/* Stat Cards */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-4">
                <div className="rounded-lg bg-muted p-3">
                  <Users className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Users</p>
                  <p className="text-2xl font-semibold">{usersTotal}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-4">
                <div className="rounded-lg bg-green-50 p-3">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Synced</p>
                  <p className="text-2xl font-semibold">{syncedCount}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-4">
                <div className="rounded-lg bg-muted p-3">
                  <Building2 className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Orgs</p>
                  <p className="text-2xl font-semibold">{orgsTotal}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-4">
                <div className="rounded-lg bg-red-50 p-3">
                  <XCircle className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Sync Errors</p>
                  <p className="text-2xl font-semibold">{errorCount}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="users">
          <TabsList>
            <TabsTrigger value="users">
              <Users className="mr-2 h-4 w-4" />
              Users ({usersTotal})
            </TabsTrigger>
            <TabsTrigger value="organizations">
              <Building2 className="mr-2 h-4 w-4" />
              Organizations ({orgsTotal})
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Users</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchUsers(usersPage)}
                  disabled={usersLoading}
                >
                  {usersLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                </Button>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Last Synced</TableHead>
                      <TableHead>Error</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">
                          {[user.firstName, user.lastName].filter(Boolean).join(' ') ||
                            '-'}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {user.email}
                        </TableCell>
                        <TableCell>
                          <SyncStatusBadge status={user.syncStatus} />
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(user.lastSyncedAt)}
                        </TableCell>
                        <TableCell>
                          {user.lastSyncError && (
                            <span
                              className="text-sm text-red-600 max-w-[200px] truncate block"
                              title={user.lastSyncError}
                            >
                              {user.lastSyncError}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSyncUser(user.id)}
                            disabled={syncingId === user.id || !user.clerkId}
                          >
                            {syncingId === user.id ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <RefreshCw className="h-3 w-3" />
                            )}
                            <span className="ml-1">Sync</span>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {users.length === 0 && !usersLoading && (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          className="text-center text-muted-foreground py-8"
                        >
                          No users found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>

                {/* Pagination */}
                {usersTotal > 50 && (
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-sm text-muted-foreground">
                      Page {usersPage} of {Math.ceil(usersTotal / 50)}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchUsers(usersPage - 1)}
                        disabled={usersPage <= 1 || usersLoading}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchUsers(usersPage + 1)}
                        disabled={
                          usersPage >= Math.ceil(usersTotal / 50) || usersLoading
                        }
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Organizations Tab */}
          <TabsContent value="organizations">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Organizations</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchOrgs(orgsPage)}
                  disabled={orgsLoading}
                >
                  {orgsLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                </Button>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Slug</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Last Synced</TableHead>
                      <TableHead>Error</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orgs.map((org) => (
                      <TableRow key={org.id}>
                        <TableCell className="font-medium">{org.name}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {org.slug || '-'}
                        </TableCell>
                        <TableCell>
                          {org.isInternal ? (
                            <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700">
                              Internal
                            </Badge>
                          ) : (
                            <Badge variant="outline">External</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <SyncStatusBadge status={org.syncStatus} />
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(org.lastSyncedAt)}
                        </TableCell>
                        <TableCell>
                          {org.lastSyncError && (
                            <span
                              className="text-sm text-red-600 max-w-[200px] truncate block"
                              title={org.lastSyncError}
                            >
                              {org.lastSyncError}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSyncOrg(org.id)}
                            disabled={syncingId === org.id || !org.clerkId}
                          >
                            {syncingId === org.id ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <RefreshCw className="h-3 w-3" />
                            )}
                            <span className="ml-1">Sync</span>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {orgs.length === 0 && !orgsLoading && (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          className="text-center text-muted-foreground py-8"
                        >
                          No organizations found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>

                {/* Pagination */}
                {orgsTotal > 50 && (
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-sm text-muted-foreground">
                      Page {orgsPage} of {Math.ceil(orgsTotal / 50)}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchOrgs(orgsPage - 1)}
                        disabled={orgsPage <= 1 || orgsLoading}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchOrgs(orgsPage + 1)}
                        disabled={
                          orgsPage >= Math.ceil(orgsTotal / 50) || orgsLoading
                        }
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </PageContainer>
  );
}
